import {
  EnumJobMatchesFilter,
  EnumJobMatchesSortingFilter,
} from "@pages/candidate/JobPage/JobMatchesPage/components/FilterSection";
import api from "../api";
import axios from "../utils/axios.instance";
import handleError from "./handle-error";
import {
  EnumSortBy,
  EnumSortOrder,
} from "@pages/JobPage/ViewByCandidate/components/FilterSection";

interface IGetJobsParams {
  page?: number;
  limit?: number;
  jobStatus?: number;
}

export const getJobs = async ({ page, limit, jobStatus }: IGetJobsParams) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append("page", page?.toString());
    if (limit) params.append("limit", limit?.toString());
    if (jobStatus) params.append("jobStatus", jobStatus?.toString());
    const { data } = await axios.post(
      api.getJobList + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getJobAverageResponseTime = async ({
  recruiterId,
}: {
  recruiterId: string;
}) => {
  try {
    const params = new URLSearchParams();
    if (recruiterId) params.append("recruiterId", recruiterId?.toString());
    const { data } = await axios.get(
      api.averageResponseTime + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface IGetJobByIdParams {
  jobId?: string;
  candidateId?: string;
}

export const getJobById = async ({ jobId, candidateId }: IGetJobByIdParams) => {
  try {
    const { data } = await axios.get(
      api.getJobDetails +
        `/${jobId}${candidateId ? `?candidateId=${candidateId}` : ""}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface IGetApplicantCountByThresholdParams {
  jobId: string;
  threshold: number;
}

export const getApplicantCountByThreshold = async ({
  jobId,
  threshold,
}: IGetApplicantCountByThresholdParams) => {
  try {
    const params = new URLSearchParams();
    if (threshold) params.append("threshold", threshold.toString());
    const { data } = await axios.get(
      api.applicantCountByThreshold(jobId) + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const shareJob = async (payload) => {
  try {
    const { data } = await axios.post(api.shareJob, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const verifyJob = async ({ email, otp }) => {
  try {
    const { data } = await axios.get(api.verifyJob + `/${email}/${otp}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const verifyJobStatus = async ({ email }) => {
  try {
    const { data } = await axios.get(api.verifyJobStatus + `/${email}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export interface IAddJobPayload {
  status: number;
  // Step1
  creationStrategy: string;
  jobRequirementId: string;
  // Step2
  title: string;
  classification: {
    main: string;
    sub: string[];
  };
  description: string;
  jobOverview: string;
  keyResponsibilities: string[];
  location: string;
  positionLevel: number;
  workPlaceOption: number;
  jobType: number;
  requiredExperience: {
    min: number;
    max: number;
  };
  qualificationEligibility: string;
  jobPostDurationDays: number;
  currency: string;
  salary: {
    min: number;
    max: number;
  };
  compensationType: string;
  companyId: string;
  showCompanyDetails: boolean;
  isSalaryHidden: boolean;
  // Step3
  industryType: string;
  salaryAndBenefits: string;
  seniorityLevel: string;
  reviewThreshold: number;
  preferredQueueSize: number;
  matchVisibilityMode: string;
  belowThresholdHandling: string;
  defaultApplicantSortBy: string;
  defaultApplicantSortOrder: string;
  queueHealthNotificationEnabled: boolean;
  queueHealthMinimum: number;
  publicationType: string;
  publishedDate: string;
  publicationTimezone: string;
}

// Every field is optional on the backend, so drafts can be saved partially.
export const addJob = async (payload: Partial<IAddJobPayload>) => {
  try {
    const { data } = await axios.post(api.addJob, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const updateJob = async (payload) => {
  try {
    const { data } = await axios.patch(api.updateJob, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const deleteJob = async (jobId) => {
  try {
    const { data } = await axios.delete(api.deleteJob + jobId);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const publishUnpublish = async (jobId, status) => {
  try {
    const { data } = await axios.patch(
      api.publishUnpublish + "/" + jobId + "/" + status,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const applyJob = async (payload) => {
  try {
    const { data } = await axios.post(api.applyJob, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export type ApplicantListTab =
  | "all"
  | "priority"
  | "in-progress"
  | "below-threshold"
  | "declined";

interface IGetApplicantListParams {
  tab?: ApplicantListTab;
  page?: number;
  limit?: number;
  searchText?: string;
  threshold?: number;
  jobId: string;
}

export const getApplicantList = async ({
  jobId,
  tab = "all",
  page = 1,
  limit = 25,
  searchText,
  threshold,
}: IGetApplicantListParams) => {
  try {
    const params = new URLSearchParams();
    if (tab) params.append("tab", tab);
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    if (searchText) params.append("searchText", searchText);
    if (threshold) params.append("threshold", threshold.toString());
    const { data } = await axios.get(api.applicantList + `/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface IJobMessageRequest {
  jobId: string;
  participantUserId: string;
  cursor?: string;
  limit?: number;
}
export const jobMessageList = async ({
  jobId,
  receiverId,
  cursor,
  limit = 25,
}: {
  jobId: string;
  receiverId: string;
  cursor?: string;
  limit?: number;
}) => {
  try {
    const params = new URLSearchParams();
    params.append("jobId", jobId);
    params.append("participantUserId", receiverId);
    if (cursor !== undefined) params.append("cursor", cursor);
    if (limit !== undefined) params.append("limit", limit.toString());
    const url = `/job/job-messages?${params.toString()}`;

    const { data } = await axios.get(url);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const jobMesasgesByJobId = async ({ receiverId, jobId }) => {
  try {
    const { data } = await axios.get(
      `/job/message/lists/${receiverId}?jobId=${jobId}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const jobMessage = async (payload) => {
  try {
    const { data } = await axios.post(api.jobMessage, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getParticipantOnlinePresence = async (userId: string) => {
  try {
    const params = new URLSearchParams();
    if (userId) params.append("userId", userId);
    const { data } = await axios.get(api.jobPresence + `?${params.toString()}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface ICandidateAppliedJobsParams {
  page: number;
  limit: number;
}
export const candidateAppliedJobs = async ({
  page,
  limit,
}: ICandidateAppliedJobsParams) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append("page", page?.toString());
    if (limit) params.append("limit", limit?.toString());
    const { data } = await axios.get(
      api.getCandidateAppliedJobs + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface ISubmitReferralRatingPayload {
  matchId: string;
  rating: number;
  comment?: string;
}

export const submitReferralRating = async (
  payload: ISubmitReferralRatingPayload,
) => {
  try {
    const { data } = await axios.post(api.submitReferralRating, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export interface IRoomGroupParams {
  conversationCategory?: "applicants" | "companies" | "referrals";
  limit?: number;
  groupType?: "job" | "participant";
  cursor?: string;
  unreadOnly?: boolean;
  activeOnly?: boolean;
  searchText?: string;
  jobId?: string;
  jobSearchText?: string;
}

export const jobMembersProfile = async ({
  conversationCategory,
  limit = 20,
  groupType = "participant",
  cursor,
  unreadOnly,
  activeOnly,
  searchText,
  jobId,
  jobSearchText,
}: IRoomGroupParams = {}) => {
  try {
    const params = new URLSearchParams();
    if (conversationCategory) params.set("conversationCategory", conversationCategory);
    if (limit !== undefined) params.append("limit", limit.toString());
    if (groupType !== undefined) params.append("groupType", groupType);
    if (cursor) params.append("cursor", cursor);
    if (unreadOnly !== undefined) params.append("unreadOnly", String(unreadOnly));
    if (activeOnly !== undefined) params.append("activeOnly", String(activeOnly));
    if (searchText?.trim()) params.append("searchText", searchText.trim());
    if (jobId) params.append("jobId", jobId);
    if (jobSearchText?.trim()) params.append("jobSearchText", jobSearchText.trim());
    const { data } = await axios.get(
      api.jobRoomGroups + `?${params.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const jobAddTracker = async (payload) => {
  try {
    const { data } = await axios.post(api.jobAddTracker, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getJobSubmissionQueue = async (jobId: string) => {
  try {
    const { data } = await axios.get(api.jobSubmission + `/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const removeJobSubmissionQueueItem = async (submissionId: string) => {
  try {
    const { data } = await axios.delete(
      api.jobSubmission + `/${submissionId}/remove`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const submitJobSubmissionQueue = async (jobId: string) => {
  try {
    const { data } = await axios.post(api.jobSubmissionQueueSubmit(jobId));
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getRecruiterApplicantDetail = async (applicationId: string, jobId?: string) => {
  try {
    const params = new URLSearchParams();
    if (jobId) params.set("jobId", jobId);
    const { data } = await axios.get(`/job/applicants/detail/${applicationId}${params.toString() ? `?${params}` : ""}`);
    return data;
  } catch (error) {
    throw handleError(error);
  }
};

export const jobGetTracker = async (jobId) => {
  try {
    const { data } = await axios.get(api.jobGetTracker + `/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const jobUpdateApplicant = async (payload) => {
  try {
    const { data } = await axios.patch(api.jobUpdateApplicant, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const unseenMessageCount = async () => {
  try {
    const { data } = await axios.get(api.unseenMessageCount);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const updateMessageSeen = async (payload) => {
  try {
    const { data } = await axios.patch(api.updateMessageSeen, payload);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface ICandidateMatchedJobsQueries {
  page?: number;
  limit?: number;
  searchText?: string;
  type: string;
  sortBy: EnumJobMatchesSortingFilter;
  favorite?: boolean;
  closed?: boolean;
  declined?: boolean;
}

export const candidateMatchedJobs = async ({
  page = 1,
  limit = 25,
  searchText,
  type,
  sortBy,
  favorite,
  closed,
  declined,
}: ICandidateMatchedJobsQueries) => {
  try {
    const params = new URLSearchParams();
    if (page) params.append("page", page?.toString());
    if (limit) params.append("limit", limit?.toString());
    if (type) params.append("type", type?.toString());
    if (searchText) params.append("searchText", searchText?.toString());
    if (sortBy) params.append("sortBy", sortBy?.toString());
    if (favorite) params.append("favorite", favorite?.toString());
    if (closed) params.append("closed", closed?.toString());
    if (declined) params.append("declined", declined?.toString());
    const { data } = await axios.get(
      api.getCandidateMatchedJobs + "?" + params?.toString(),
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const markUnmarkFavoriteMatchedJobs = async (payload: {
  isMarkFavorite: boolean;
  matchId: string;
  tag: string;
}) => {
  try {
    const { data } = await axios.post(
      api.markUnmarkFavoriteMatchedJobs,
      payload,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getMatchedJobById = async (jobId) => {
  try {
    const { data } = await axios.get(api.getJobDetails + `/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getMatchJobSkills = async (jobId) => {
  try {
    const { data } = await axios.get(api.matchJobSkils + `/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const jobSeen = async (jobId) => {
  try {
    const { data } = await axios.post(api.jobSeen, jobId);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const getJobContactMatches = async (jobId) => {
  try {
    const { data } = await axios.get(`${api.contactMatches}/${jobId}`);
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const confirmExclusiveRepresentation = async (payload) => {
  try {
    const { data } = await axios.post(
      api.confirmExclusiveRepresentation,
      payload,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface IJobListViewByCandidateParams {
  page: number;
  limit: number;
  searchText?: string;
  sortBy: EnumSortBy;
  sortOrder: EnumSortOrder;
}

export const jobListViewByCandidate = async ({
  page = 1,
  limit = 25,
  searchText,
  sortBy,
  sortOrder,
}: IJobListViewByCandidateParams) => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append("limit", limit?.toString());
    if (page) params.append("page", page?.toString());
    if (searchText) params.append("searchText", searchText?.toString());
    if (sortBy) params.append("sortBy", sortBy?.toString());
    if (sortOrder) params.append("sortOrder", sortOrder?.toString());
    const { data } = await axios.get(
      api.jobListViewByCandidate + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

export const recruiterCandidateAppliedJobs = async (
  candidateId: string,
  {
    sortBy,
    sortOrder,
    searchText,
  }: {
    sortBy?: string;
    sortOrder?: string;
    searchText?: string;
  } = {},
) => {
  try {
    const params = new URLSearchParams();
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (searchText) params.append("searchText", searchText);
    const query = params.toString();
    const { data } = await axios.get(
      `${api.recruiterCandidateAppliedJobs}/${candidateId}${
        query ? `?${query}` : ""
      }`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

interface IJobListByCompanyParams {
  hiringCompanyId: string;
  page: number;
  limit: number;
  jobStatus?: number | undefined;
}
export const jobListByCompany = async ({
  hiringCompanyId,
  page = 1,
  limit = 25,
  jobStatus,
}: IJobListByCompanyParams) => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append("limit", limit?.toString());
    if (page) params.append("page", page?.toString());
    if (jobStatus) params.append("jobStatus", jobStatus?.toString());
    const { data } = await axios.get(
      api.jobListByCompany(hiringCompanyId) + `?${params?.toString()}`,
    );
    return data;
  } catch (err) {
    throw handleError(err);
  }
};

// Job Creation API starts here

// Job Creation API ends here
